import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ImagePlus, Plus, Trash2 } from "lucide-react";
import DashboardLayout from "../../layouts/DashboardLayout";
import { api, mediaUrl, uploadImage } from "../../api/client";

const blank = {
  name: "",
  tagline: "",
  type: "both",
  description: "",
  city: "",
  address: "",
  phone: "",
  email: "",
  coverImage: "",
  gallery: [],
  amenities: [],
  capacity: 100,
  startingPrice: 0,
  currency: "LKR",
  rooms: [],
  packages: [],
  published: false,
};

export default function VenueEditor() {
  const [form, setForm] = useState(blank);
  const [slug, setSlug] = useState("");
  const [amenity, setAmenity] = useState("");
  const [galleryUrl, setGalleryUrl] = useState("");
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api("/api/owner/venue").then((venue) => {
      if (venue) {
        setForm({ ...blank, ...venue });
        setSlug(venue.slug);
      }
    });
  }, []);

  function update(key, value) {
    setForm((prev) => ({
      ...prev,
      [key]: typeof value === "function" ? value(prev[key]) : value,
    }));
  }

  function addRoom() {
    update("rooms", [
      ...form.rooms,
      { id: crypto.randomUUID(), name: "New room", type: "room", capacity: 2, price: 0, count: 1, image: "" },
    ]);
  }

  function addPackage() {
    update("packages", [
      ...form.packages,
      { id: crypto.randomUUID(), name: "New package", guests: 80, price: 0, includes: [""] },
    ]);
  }

  async function save(event) {
    event.preventDefault();
    setSaving(true);
    setMessage("");
    try {
      const saved = await api("/api/owner/venue", {
        method: "PUT",
        body: JSON.stringify({
          ...form,
          gallery: form.gallery.filter(Boolean),
          amenities: form.amenities.filter(Boolean),
        }),
      });
      setForm({ ...blank, ...saved });
      setSlug(saved.slug);
      setMessage("Venue page saved.");
    } catch (err) {
      setMessage(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <DashboardLayout title="Dress your venue">
      <form onSubmit={save} className="space-y-8">
        <section className="card p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="font-serif text-2xl">Profile</h2>
            {slug && (
              <Link to={`/u/${slug}`} className="text-sm text-rose">
                Public page /u/{slug}
              </Link>
            )}
          </div>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <Field label="Venue name" value={form.name} onChange={(v) => update("name", v)} />
            <Field label="Tagline" value={form.tagline} onChange={(v) => update("tagline", v)} />
            <div>
              <label className="label">Type</label>
              <select className="field" value={form.type} onChange={(e) => update("type", e.target.value)}>
                <option value="hotel">Hotel</option>
                <option value="wedding-hall">Wedding hall</option>
                <option value="both">Hotel + hall</option>
              </select>
            </div>
            <Field label="City" value={form.city} onChange={(v) => update("city", v)} />
            <Field label="Address" value={form.address} onChange={(v) => update("address", v)} />
            <Field label="Phone" value={form.phone} onChange={(v) => update("phone", v)} />
            <Field label="Public email" value={form.email} onChange={(v) => update("email", v)} />
            <Field
              label="Starting price"
              type="number"
              value={form.startingPrice}
              onChange={(v) => update("startingPrice", v)}
            />
            <Field
              label="Capacity"
              type="number"
              value={form.capacity}
              onChange={(v) => update("capacity", v)}
            />
            <div className="md:col-span-2">
              <label className="label">Story</label>
              <textarea
                className="field min-h-[120px]"
                value={form.description}
                onChange={(e) => update("description", e.target.value)}
              />
            </div>
          </div>
        </section>

        <section className="card p-6">
          <h2 className="font-serif text-2xl">Images</h2>
          <div className="mt-4">
            <ImagePicker
              label="Cover image"
              value={form.coverImage}
              onChange={(v) => update("coverImage", v)}
              previewClassName="h-52 w-full rounded-[1.6rem] object-cover"
            />
          </div>
          <div className="mt-6">
            <label className="label">Gallery</label>
            <div className="flex flex-wrap gap-2">
              <input
                className="field flex-1"
                placeholder="Paste a gallery image URL"
                value={galleryUrl}
                onChange={(e) => setGalleryUrl(e.target.value)}
              />
              <button
                type="button"
                className="btn-ghost"
                onClick={() => {
                  if (!galleryUrl.trim()) return;
                  update("gallery", (gallery) => [...gallery, galleryUrl.trim()]);
                  setGalleryUrl("");
                }}
              >
                <Plus size={16} /> Add
              </button>
              <FileButton
                multiple
                onFiles={async (files) => {
                  const urls = [];
                  for (const file of files) {
                    urls.push(await uploadImage(file));
                  }
                  update("gallery", (gallery) => [...gallery, ...urls]);
                }}
              >
                <ImagePlus size={16} /> Upload
              </FileButton>
            </div>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            {form.gallery.map((url, index) => (
              <div key={`${url}-${index}`} className="relative overflow-hidden rounded-2xl">
                <img src={mediaUrl(url)} alt="" className="h-28 w-full object-cover" />
                <button
                  type="button"
                  className="absolute right-2 top-2 rounded-full bg-white p-1"
                  onClick={() =>
                    update(
                      "gallery",
                      form.gallery.filter((_, i) => i !== index)
                    )
                  }
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </section>

        <section className="card p-6">
          <h2 className="font-serif text-2xl">Amenities</h2>
          <div className="mt-4 flex gap-2">
            <input
              className="field"
              value={amenity}
              placeholder="Valet, bridal suite…"
              onChange={(e) => setAmenity(e.target.value)}
            />
            <button
              type="button"
              className="btn-ghost"
              onClick={() => {
                if (!amenity.trim()) return;
                update("amenities", [...form.amenities, amenity.trim()]);
                setAmenity("");
              }}
            >
              Add
            </button>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            {form.amenities.map((item) => (
              <button
                type="button"
                key={item}
                className="chip bg-blush/70"
                onClick={() =>
                  update(
                    "amenities",
                    form.amenities.filter((row) => row !== item)
                  )
                }
              >
                {item} ×
              </button>
            ))}
          </div>
        </section>

        <section className="card p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-2xl">Rooms</h2>
            <button type="button" className="btn-ghost" onClick={addRoom}>
              <Plus size={16} /> Room
            </button>
          </div>
          <div className="mt-4 space-y-4">
            {form.rooms.map((room, index) => (
              <div key={room.id} className="grid gap-3 rounded-2xl bg-cream p-4 md:grid-cols-5">
                <input
                  className="field"
                  value={room.name}
                  onChange={(e) => patchList("rooms", index, { name: e.target.value })}
                />
                <input
                  className="field"
                  type="number"
                  placeholder="Price"
                  value={room.price}
                  onChange={(e) => patchList("rooms", index, { price: Number(e.target.value) })}
                />
                <input
                  className="field"
                  type="number"
                  placeholder="Capacity"
                  value={room.capacity}
                  onChange={(e) => patchList("rooms", index, { capacity: Number(e.target.value) })}
                />
                <input
                  className="field"
                  type="number"
                  placeholder="Count"
                  value={room.count}
                  onChange={(e) => patchList("rooms", index, { count: Number(e.target.value) })}
                />
                <button
                  type="button"
                  className="btn-ghost"
                  onClick={() =>
                    update(
                      "rooms",
                      form.rooms.filter((row) => row.id !== room.id)
                    )
                  }
                >
                  Remove
                </button>
                <div className="md:col-span-5">
                  <ImagePicker
                    value={room.image || ""}
                    onChange={(v) => patchList("rooms", index, { image: v })}
                    previewClassName="h-28 w-full rounded-2xl object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="card p-6">
          <div className="flex items-center justify-between">
            <h2 className="font-serif text-2xl">Wedding & party packages</h2>
            <button type="button" className="btn-ghost" onClick={addPackage}>
              <Plus size={16} /> Package
            </button>
          </div>
          <div className="mt-4 space-y-4">
            {form.packages.map((pack, index) => (
              <div key={pack.id} className="space-y-3 rounded-2xl bg-cream p-4">
                <div className="grid gap-3 md:grid-cols-4">
                  <input
                    className="field md:col-span-2"
                    value={pack.name}
                    onChange={(e) => patchList("packages", index, { name: e.target.value })}
                  />
                  <input
                    className="field"
                    type="number"
                    value={pack.guests}
                    onChange={(e) => patchList("packages", index, { guests: Number(e.target.value) })}
                  />
                  <input
                    className="field"
                    type="number"
                    value={pack.price}
                    onChange={(e) => patchList("packages", index, { price: Number(e.target.value) })}
                  />
                </div>
                <input
                  className="field"
                  value={(pack.includes || []).join(", ")}
                  placeholder="Includes, comma separated"
                  onChange={(e) =>
                    patchList("packages", index, {
                      includes: e.target.value.split(",").map((item) => item.trim()).filter(Boolean),
                    })
                  }
                />
                <button
                  type="button"
                  className="text-sm text-rose"
                  onClick={() =>
                    update(
                      "packages",
                      form.packages.filter((row) => row.id !== pack.id)
                    )
                  }
                >
                  Remove package
                </button>
              </div>
            ))}
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-4">
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.published}
              onChange={(e) => update("published", e.target.checked)}
            />
            Publish this page
          </label>
          <button className="btn-rose" disabled={saving}>
            {saving ? "Saving…" : "Save venue"}
          </button>
          {message && <p className="text-sm text-plum">{message}</p>}
        </div>
      </form>
    </DashboardLayout>
  );

  function patchList(key, index, patch) {
    update(key, (rows) =>
      (rows || []).map((row, i) => (i === index ? { ...row, ...patch } : row))
    );
  }
}

function Field({ label, value, onChange, type = "text" }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input
        type={type}
        className="field"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function FileButton({ onFiles, children, multiple = false }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  async function handleChange(event) {
    const files = [...(event.target.files || [])];
    event.target.value = "";
    if (!files.length) return;
    setBusy(true);
    setError("");
    try {
      await onFiles(files);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <label className={`btn-ghost cursor-pointer ${busy ? "opacity-60" : ""}`}>
        <input
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          className="hidden"
          multiple={multiple}
          disabled={busy}
          onChange={handleChange}
        />
        {busy ? "Uploading…" : children}
      </label>
      {error && <p className="mt-1 text-sm text-rose">{error}</p>}
    </div>
  );
}

function ImagePicker({ label, value, onChange, previewClassName }) {
  return (
    <div>
      {label && <label className="label">{label}</label>}
      {value ? (
        <img src={mediaUrl(value)} alt="" className={`mb-3 ${previewClassName}`} />
      ) : label ? (
        <div
          className={`mb-3 grid place-items-center bg-cream text-sm text-ink/40 ${previewClassName}`}
        >
          No image yet
        </div>
      ) : null}
      {label && (
        <p className="mb-2 text-xs text-ink/45">
          Upload a file or paste a URL, then save the venue.
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <input
          className="field flex-1"
          placeholder="Paste an image URL"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
        <FileButton onFiles={async ([file]) => onChange(await uploadImage(file))}>
          <ImagePlus size={16} /> Upload
        </FileButton>
      </div>
    </div>
  );
}
